import { useEffect, useRef, useState, type ReactNode } from 'react';

export type RevealDirection = 'up' | 'down' | 'left' | 'right' | 'none';

const hiddenTransform: Record<RevealDirection, string> = {
  up: 'translate-y-6',
  down: '-translate-y-6',
  left: 'translate-x-8',
  right: '-translate-x-8',
  none: '',
};

/**
 * Lightweight scroll-reveal wrapper — IntersectionObserver only, no animation
 * library dependency (keeps this static site's JS payload lean). Fades + slides
 * children into place the first time they enter the viewport, from a
 * configurable direction ('none' = pure fade). Respects prefers-reduced-motion
 * by showing content immediately, no observer needed.
 */
export function Reveal({
  children,
  delay = 0,
  direction = 'up',
  className = '',
}: {
  children: ReactNode;
  delay?: number;
  direction?: RevealDirection;
  className?: string;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        visible ? 'translate-x-0 translate-y-0 opacity-100' : `${hiddenTransform[direction]} opacity-0`
      } ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
    >
      {children}
    </div>
  );
}
